import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { DEVICE_NAME_MIN_LENGTH, DEVICE_NAME_MAX_LENGTH } from 'entities/device';
import {
	createEditDeviceDetailedMutation,
	DeviceDetailed,
	EditDeviceDetailedMutationParams,
	EntityWithName,
} from 'shared/api';
import {
	maxLengthRule,
	minLengthRule,
	notEmptyRule,
	notify,
	createModalFormModel,
	ModalFormModel,
} from 'shared/lib';

export type EditDeviceDetailedFormValue = {
	name: string;
};

export type EditDeviceDetailedModel = ModalFormModel<
	DeviceDetailed,
	EditDeviceDetailedFormValue
> & {
	editDeviceDetailedMutation: Mutation<
		EditDeviceDetailedMutationParams,
		EntityWithName,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createEditDeviceDetailedModel = (): EditDeviceDetailedModel => {
	const editDeviceDetailedMutation = createEditDeviceDetailedMutation();

	const model = createModalFormModel<DeviceDetailed, EditDeviceDetailedFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(DEVICE_NAME_MIN_LENGTH),
					maxLengthRule(DEVICE_NAME_MAX_LENGTH),
				],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = editDeviceDetailedMutation;

	sample({
		clock: model.modalButtonClicked,
		fn: (device) => ({ name: device.name }),
		target: [model.form.setValues, model.form.setInitialValues],
	});

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			device: model.$modalData,
		},
		filter: ({ device }) => device !== null,
		fn: ({ values, device }) => ({
			id: device?.id ?? 0,
			name: values.name,
		}),
		target: editDeviceDetailedMutation.start,
	});

	chain(editDeviceDetailedMutation.finished.success, model.switcher.disable);

	notify(editDeviceDetailedMutation.finished.success, 'edit-device.success');

	chain(model.modalClosed, editDeviceDetailedMutation.reset);

	return {
		editDeviceDetailedMutation,
		$pending,
		...model,
	};
};
